// src/store/modules/consent.js
//
// Vuex 3 module that owns:
//   - Detection of the Global Privacy Control (GPC) signal
//   - User cookie-consent state (per category)
//   - Persistence to localStorage with a version stamp
//
// Bump CONSENT_VERSION when the policy / category set materially changes;
// the module will then re-prompt all users.

const STORAGE_KEY = 'app.consent.v1';
const CONSENT_VERSION = '1.0.0';

// `necessary` is always on. The others are opt-in unless the user agrees,
// and `analytics` / `advertising` are force-disabled while GPC is active.
const DEFAULT_CATEGORIES = Object.freeze({
  necessary: true,
  functional: false,
  analytics: false,
  advertising: false,
});

const GPC_LOCKED_CATEGORIES = ['analytics', 'advertising'];

function detectGPC() {
  if (typeof navigator === 'undefined') return false;
  // Standard, set by browsers / extensions implementing the GPC spec.
  return navigator.globalPrivacyControl === true;
}

// Property access on window.localStorage can throw a SecurityError in
// restricted contexts (sandboxed iframes, Firefox strict tracking protection,
// some private-mode configurations). A plain `typeof` guard is not enough
// because the access itself triggers the throw — wrap it.
function getStorage() {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage || null;
  } catch (e) {
    return null;
  }
}

function loadFromStorage() {
  const ls = getStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function persist(state) {
  const ls = getStorage();
  if (ls) {
    try {
      ls.setItem(
          STORAGE_KEY,
          JSON.stringify({
            version: CONSENT_VERSION,
            categories: state.categories,
            consentGiven: state.consentGiven,
            consentTimestamp: state.consentTimestamp,
            gpcRespected: state.gpcRespected,
            isOpen: state.isOpen,
          })
      );
    } catch (e) {
      // quota exceeded / storage disabled — fail silently
    }
  }
  broadcastConsent(state);
}

// Lets the plain <script> pixel loaders in public/index.html (which run
// outside Vue/Vuex) react to consent changes without a page reload.
function broadcastConsent(state) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent('consent:updated', {
      detail: { categories: state.categories, gpcActive: state.gpcSignal },
    }));
  } catch (e) {
    // noop — e.g. CustomEvent unsupported
  }
}

function applyGpcMask(categories, gpcActive) {
  if (!gpcActive) return categories;
  const masked = { ...categories };
  GPC_LOCKED_CATEGORIES.forEach((k) => { masked[k] = false; });
  return masked;
}

export default {
  namespaced: true,

  state: () => ({
    initialized: false,
    gpcSignal: false, // raw signal from navigator
    gpcRespected: false, // did the persisted consent honor GPC?
    consentGiven: false,
    consentTimestamp: null,
    categories: { ...DEFAULT_CATEGORIES },
    isOpen: false,
    version: CONSENT_VERSION
  }),

  getters: {
    isInitialized: s => s.initialized,
    gpcActive: s => !!s.gpcSignal,
    isOpen: s => !!s.isOpen,
    requiresPrompt: s => !s.consentGiven,
    categories: s => s.categories,
    consentMeta: s => ({
      version: s.version,
      timestamp: s.consentTimestamp,
      gpcRespected: s.gpcRespected,
    }),
    // Generic gate: store.getters['consent/canUse']('analytics')
    canUse: (s) => (category) => {
      if (category === 'necessary') return true;
      return s.categories[category] === true;
    },
    canUseFunctional:  (s) => s.categories.functional === true,
    canUseAnalytics:   (s) => s.categories.analytics === true,
    canUseAdvertising: (s) => s.categories.advertising === true,
  },

  mutations: {
    SET_GPC(state, value) {
      state.gpcSignal = !!value;
    },
    SET_CATEGORIES(state, categories) {
      state.categories = {
        ...DEFAULT_CATEGORIES,
        ...categories,
        necessary: true, // always enforced
      };
    },
    SET_CONSENT(state, { consentGiven, timestamp, gpcRespected }) {
      state.consentGiven = consentGiven;
      state.consentTimestamp = timestamp;
      state.gpcRespected = !!gpcRespected;
    },
    SET_INITIALIZED(state, value) {
      state.initialized = !!value;
    },
    SET_OPEN(state, v) {
      state.isOpen = !!v;
    },
    RESET(state) {
      state.consentGiven = false;
      state.consentTimestamp = null;
      state.categories = { ...DEFAULT_CATEGORIES };
      state.gpcRespected = false;
    },
  },

  actions: {
    // Call once during app bootstrap (before mount).
    initialize({ commit, state, dispatch }) {
      const gpc = detectGPC();
      commit('SET_GPC', gpc);

      const stored = loadFromStorage();

      if (stored) {
        commit('SET_CATEGORIES', applyGpcMask(stored.categories, gpc));
        commit('SET_CONSENT', {
          isOpen: false,
          consentGiven: stored.consentGiven,
          timestamp: stored.consentTimestamp,
          gpcRespected: !!stored.gpcRespected || gpc,
        });
        persist(state);

        // If a category got masked off by GPC, give listeners a chance to clean up.
        if (gpc && !stored.gpcRespected) {
          dispatch('purgeNonConsented');
        }
      } else if (gpc) {
        // No prior consent + GPC active: pre-mask defaults, but DO NOT mark
        // consent as given. The banner should still appear so the user can
        // choose for non-locked categories (e.g. functional).
        commit('SET_CATEGORIES', applyGpcMask(DEFAULT_CATEGORIES, true));
      }

      commit('SET_INITIALIZED', true);
    },

    openPanel({ commit }) {
      commit('SET_OPEN', true);
    },
    closePanel({ commit }) {
      commit('SET_OPEN', false);
    },

    acceptAll({ commit, state, dispatch }) {
      const gpc = state.gpcSignal;
      const categories = applyGpcMask(
        {
          necessary: true,
          functional: true,
          analytics: true,
          advertising: true
        },
        gpc
      );
      commit('SET_CATEGORIES', categories);
      commit('SET_CONSENT', {
        consentGiven: true,
        timestamp: Date.now(),
        gpcRespected: gpc,
        isOpen: false,
      });
      persist(state);
      dispatch('purgeNonConsented');
    },

    rejectAll({ commit, state, dispatch }) {
      commit('SET_CATEGORIES', { ...DEFAULT_CATEGORIES });
      commit('SET_CONSENT', {
        consentGiven: true,
        timestamp: Date.now(),
        gpcRespected: state.gpcSignal,
        isOpen: false,
      });
      persist(state);
      dispatch('purgeNonConsented');
    },

    updateCategories({ commit, state, dispatch }, categories) {
      const gpc = state.gpcSignal;
      const merged = applyGpcMask(
        { ...DEFAULT_CATEGORIES, ...categories, necessary: true },
        gpc
      );
      commit('SET_CATEGORIES', merged);
      commit('SET_CONSENT', {
        consentGiven: true,
        timestamp: Date.now(),
        gpcRespected: gpc,
        isOpen: false,
      });
      persist(state);
      dispatch('purgeNonConsented');
    },

    revokeConsent({ commit }) {
      commit('RESET');
      const ls = getStorage();
      if (ls) {
        try { ls.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
      }
    },

    // Hook for cookie/storage cleanup; the storage util listens for this
    // via store.subscribeAction. Action body is intentionally empty so
    // callers only need to subscribe.
    // eslint-disable-next-line no-empty-pattern
    purgeNonConsented() { /* observable */ },
  },
};

export {
  STORAGE_KEY,
  CONSENT_VERSION,
  DEFAULT_CATEGORIES,
  GPC_LOCKED_CATEGORIES,
};